<template>
  <section class="settings-card">
    <div class="card-header">
      <div>
        <h2 class="card-title blue">团队成员管理</h2>
        <p class="card-desc">管理该项目下的所有参与人员及其角色权限</p>
      </div>
      <el-button type="primary" size="small" class="add-btn" @click="showAddDialog" v-if="canManage">
        <i class="el-icon-s-custom"></i> 添加新成员
      </el-button>
    </div>
    <div class="member-table-wrap">
      <table class="member-table">
        <thead>
          <tr><th>成员姓名</th><th>角色类型</th><th>电子邮箱</th><th>加入时间</th><th>操作</th></tr>
        </thead>
        <tbody>
          <tr v-for="m in pagedList" :key="m.id">
            <td>
              <div class="member-name">
                <span class="member-avatar" :style="{ background: getAvatarColor(m) }">{{ getInitial(m) }}</span>
                <span>{{ m.name || m.Username || m.username || '-' }}</span>
              </div>
            </td>
            <td>
              <span class="role-badge" :style="{ background: getRoleBg(m.Role || m.role), color: getRoleColor(m.Role || m.role) }">
                {{ getRoleLabel(m.Role || m.role) }}
              </span>
            </td>
            <td class="td-muted">{{ m.email || m.Email || '-' }}</td>
            <td class="td-muted">{{ formatDate(m.CreatedTime || m.createdTime) }}</td>
            <td>
              <i class="el-icon-edit-outline action-icon" title="编辑角色" @click="changeRole(m)" v-if="canManage"></i>
              <i class="el-icon-delete action-icon danger" title="移除" @click="removeMember(m)" v-if="canManage && !isSelf(m)"></i>
            </td>
          </tr>
          <tr v-if="members.length === 0">
            <td colspan="5" style="text-align:center;color:#94a3b8;padding:32px 0;">暂无成员</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="member-pagination" v-if="members.length > pageSize">
      <el-pagination layout="total, prev, pager, next" :total="members.length" :page-size="pageSize" :current-page.sync="currentPage" @current-change="p => currentPage = p" />
    </div>

    <!-- 添加成员弹窗 -->
    <el-dialog :visible.sync="addDialogVisible" width="480px" append-to-body custom-class="add-member-dialog" :show-close="false">
      <div class="dlg-header">
        <h3 class="dlg-title">添加成员</h3>
        <i class="el-icon-close dlg-close" @click="addDialogVisible = false"></i>
      </div>
      <div class="dlg-body">
        <div class="dlg-section">
          <label class="dlg-label">选择用户</label>
          <el-select v-model="memberForm.userIds" filterable multiple placeholder="输入用户名搜索，可多选" style="width:100%" class="dlg-select">
            <el-option v-for="u in availableUsers" :key="u.uid" :label="u.name ? (u.name + ' (' + u.username + ')') : u.username" :value="u.uid" />
          </el-select>
          <p class="dlg-hint">可同时选择多个用户，统一分配角色。已参与的成员不会出现。</p>
        </div>
        <div class="dlg-section">
          <label class="dlg-label">角色</label>
          <div class="role-pills">
            <span v-for="r in teamRoleOptions" :key="r.value" class="role-pill" :class="{ active: memberForm.role === r.value }" @click="memberForm.role = r.value">{{ r.label }}</span>
          </div>
        </div>
        <div class="role-info-box" v-if="currentRoleInfo">
          <div class="role-info-header"><i class="el-icon-info role-info-icon"></i><strong>{{ currentRoleInfo.title }} 权限说明</strong></div>
          <p class="role-info-desc">{{ currentRoleInfo.desc }}</p>
        </div>
      </div>
      <div class="dlg-footer">
        <el-button @click="addDialogVisible = false" class="dlg-cancel-btn">取消</el-button>
        <el-button type="primary" @click="handleAddMember" class="dlg-confirm-btn">确定添加</el-button>
      </div>
    </el-dialog>

    <!-- 修改角色弹窗 -->
    <el-dialog :visible.sync="editRoleDialogVisible" width="480px" append-to-body custom-class="add-member-dialog" :show-close="false">
      <div class="dlg-header">
        <h3 class="dlg-title">修改角色</h3>
        <i class="el-icon-close dlg-close" @click="editRoleDialogVisible = false"></i>
      </div>
      <div class="dlg-body">
        <div v-if="editRoleUser" class="dlg-section" style="margin-bottom:12px">
          <span style="color:#64748b;font-size:14px">成员：</span>
          <strong style="color:#0f172a">{{ editRoleUser.name || editRoleUser.Username || editRoleUser.username }}</strong>
        </div>
        <div class="dlg-section">
          <label class="dlg-label">角色</label>
          <div class="role-pills">
            <span v-for="r in teamRoleOptions" :key="r.value" class="role-pill" :class="{ active: editRoleValue === r.value }" @click="editRoleValue = r.value">{{ r.label }}</span>
          </div>
        </div>
        <div class="role-info-box" v-if="editRoleInfo">
          <div class="role-info-header"><i class="el-icon-info role-info-icon"></i><strong>{{ editRoleInfo.title }} 权限说明</strong></div>
          <p class="role-info-desc">{{ editRoleInfo.desc }}</p>
        </div>
      </div>
      <div class="dlg-footer">
        <el-button @click="editRoleDialogVisible = false" class="dlg-cancel-btn">取消</el-button>
        <el-button type="primary" @click="doChangeRole" class="dlg-confirm-btn">确定</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import { addProjectMember, updateProjectMember, deleteProjectMember, getUserList } from '@/app/index/api/project/manager'
import { roleOptions, roleDescriptions, formatDate, getInitial, getAvatarColor, getRoleBg, getRoleColor, getRoleLabel } from './settingsHelpers'

export default {
  name: 'MemberSection',
  props: {
    projectId: { type: [String, Number], required: true },
    members: { type: Array, default: () => [] },
    currentUser: { type: Object, default: () => ({}) },
    canManage: { type: Boolean, default: false },
    excludedUserIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      userList: [],
      addDialogVisible: false,
      memberForm: { userIds: [], role: 'member' },
      editRoleDialogVisible: false,
      editRoleUser: null,
      editRoleValue: '',
      roleOptions,
      roleDescriptions
    }
  },
  computed: {
    pagedList() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.members.slice(start, start + this.pageSize)
    },
    availableUsers() {
      const memberUids = new Set(this.members.map(m => m.UserId || m.userId || m.uid))
      const excluded = new Set(this.excludedUserIds || [])
      return this.userList.filter(u => !memberUids.has(u.uid) && !excluded.has(u.uid))
    },
    teamRoleOptions() {
      return (this.roleOptions || []).filter(r => String(r.value || '').toLowerCase() !== 'agent')
    },
    currentRoleInfo() { return roleDescriptions[this.memberForm.role] || null },
    editRoleInfo() { return roleDescriptions[this.editRoleValue] || null }
  },
  methods: {
    formatDate, getInitial, getAvatarColor, getRoleBg, getRoleColor, getRoleLabel,
    isSelf(member) {
      const uid = this.currentUser.uid || this.currentUser.id
      return uid && (member.UserId || member.userId || member.uid) == uid
    },
    async showAddDialog() {
      this.memberForm = { userIds: [], role: 'member' }
      this.addDialogVisible = true
      try {
        const res = await getUserList({ Page: 1, Size: 999 })
        const data = res?.data?.data || res?.data
        this.userList = (data?.List || data?.list || data || []).map(u => ({
          uid: u.uid, username: u.username, name: u.name, email: u.email
        }))
      } catch (e) { console.error('加载用户列表失败', e) }
    },
    // 🔧 Bug修复: 添加失败不中断后续用户
    async handleAddMember() {
      if (!this.memberForm.userIds || this.memberForm.userIds.length === 0) {
        return this.$message.warning('请选择用户')
      }
      let successCount = 0, failCount = 0
      for (const uid of this.memberForm.userIds) {
        try {
          await addProjectMember({ projectId: this.projectId, userId: uid, role: this.memberForm.role })
          successCount++
        } catch (e) {
          failCount++
          console.error(`添加用户 ${uid} 失败`, e)
        }
      }
      if (failCount > 0) {
        this.$message.warning(`成功${successCount}个, 失败${failCount}个`)
      } else {
        this.$message.success(`成功添加 ${successCount} 名成员`)
      }
      this.addDialogVisible = false
      this.$emit('refresh')
    },
    changeRole(member) {
      this.editRoleUser = member
      this.editRoleValue = member.Role || member.role || 'member'
      this.editRoleDialogVisible = true
    },
    async doChangeRole() {
      try {
        await updateProjectMember({ id: this.editRoleUser.id, role: this.editRoleValue })
        this.$message.success('角色修改成功')
        this.editRoleDialogVisible = false
        this.$emit('refresh')
      } catch (e) { this.$message.error(e?.data?.message || '修改失败') }
    },
    removeMember(member) {
      const name = member.name || member.Username || member.username || '该成员'
      this.$confirm(`确定要移除 ${name} 吗？`, '提示', { type: 'warning', customClass: 'settings-confirm-dialog' })
        .then(async () => {
          try {
            await deleteProjectMember(member.id)
            this.$message.success('成员已移除')
            this.$emit('refresh')
          } catch (e) { this.$message.error(e?.data?.message || e?.message || '移除失败') }
        }).catch(() => {})
    }
  }
}
</script>
